import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ProcureGetter } from '@/store/procure/procure.module';
import { Procure } from '@/store/procure/procure.model';
import { ProgressAction, ProgressGetter } from '@/store/progress/progress.module';
import { Progress } from '@/store/progress/progress.model';
import { ProgressSchema } from '@/store/progress/progress.validator';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectProgressFormComponent',
  components: {
    Datepicker,
    BaseModalComponent
  },
  props: {
    projectId: {
      type: Number,
    },
    progressId: {
      type: Number,
    },
  },
  emits: [
    'refresh'
  ],
  setup: (props, context) => {
    const store = useStore();
    const isEditMode = computed(() => props.progressId != null);
    const procure = computed<Procure>(() => store.getters[ProcureGetter.GET]);
    const progresses = computed<Progress[]>(() => store.getters[ProgressGetter.ALL]);
    const progress = computed<Progress | null | undefined>(() => progresses.value?.find(m => m.id === props.progressId));
    const title = computed(() => isEditMode.value ? 'แก้ไขผลการเบิกจ่ายงบ' : 'เพิ่มผลการเบิกจ่ายงบ');
    const progressForm = ref<InstanceType<typeof BaseModalComponent>>();

    const balance = reactive({
      budget: computed(() => procure.value?.realBudget - (progresses.value?.reduce((total, m) => total + m.spent, 0) - ((progress.value) ? progress.value?.spent : 0))),
      percent: computed(() => 100 - (progresses.value?.reduce((total, m) => total + m.percent, 0) - ((progress.value) ? progress.value?.percent : 0)))
    });

    // form
    interface Form {
      id?: number | null;
      projectId?: number;
      spentTypeId?: number;
      spentDate?: Date | null;
      spent?: number | null;
      percent?: number | null;
      remark?: string | null;
    }
    const formData = reactive<Form>({
      projectId: props.projectId,
      spentTypeId: 1,
      spentDate: null,
      spent: null,
      percent: null,
      remark: '',
    });
    const validator = computed(() => ProgressSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      spentDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'spentDate')),
      spent: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'spent') || (formData.spent ? formData.spent : 0) > balance.budget),
      percent: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'percent') || (formData.percent ? formData.percent : 0) > balance.percent),
    });
    const isValid = computed(() => !errors.spentDate && !errors.percent && !errors.spent);
    const isLoading = computed(() => store.getters[ProgressGetter.STATUS] === 'init' || store.getters[ProgressGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.progressId;
          formData.spentDate = progress.value?.spentDate;
          formData.spent = progress.value?.spent;
          formData.percent = progress.value?.percent;
          formData.remark = progress.value?.remark;
        }
        resolve();
      })
      .then(() => progressForm.value?.show());
    };

    // hide modal
    const hide = () => {
      progressForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.spentDate = null;
        formData.spent = null;
        formData.percent = null;
        formData.remark = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(ProgressAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(ProgressAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    const refresh = () => {
      context.emit('refresh');
    };

    return {
      balance,
      formData,
      progressForm,
      title,
      isLoading,
      errors,
      submited,
      show,
      hide,
      unsubmit,
      clearForm,
      submitClick,
      refresh,
    };
  },
});
